import {
  isValidDateOfBirth,
  mapPatientInformationToPatientGroupData,
  usePatientGroupForm,
  useTranslation,
  defaultPatientInfoData,
} from "hhn-commons";
import * as React from "react";
import { useEffect, useState } from "react";
import PatientGroupStatus from "./PatientGroupStatus";
import {
  AddPatientInfoLayout,
  FormControlLayout,
  HorizontalLayout,
  SearchField,
  SetForthButton,
  SubmitButtonWrapper,
  SyncButton,
  VerticalLayout,
} from "./AddPatientGroup.style";
import { IGroupInfo } from "@home-health-notify/domain";
import { DrawerDivider } from "../layout/RightDrawer.style";
import { SocStarterField } from "./SocStarterField";
import { ReferralInfoField } from "./ReferralInfoField";
import { LupaField } from "./LupaField";
import { AboutField } from "./AboutField";
import { AddInfo } from "./addInfoField/AddInfo";
import { InsuranceField } from "./InsuranceField";
import AddressField from "./addressField/AddressField";
import { InputFirstName } from "./addPatientInfo/InputFirstName";
import { InputLastName } from "./addPatientInfo/InputLastName";
import { InputMedicalRecordNumber } from "./addPatientInfo/InputMedicalRecordNumber";
import { InputDOB } from "./addPatientInfo/InputDOB";
import { InputPatientTag } from "./addPatientInfo/InputPatientTag";
import { InputPhoneNumber } from "./addPatientInfo/InputPhoneNumber";
import InputApartmentNumber from "./addPatientInfo/InputApartmentNumber";
import { cloneDeep } from "lodash";
import { InputMRN } from "./addPatientInfo/InputMRN";
import { RecentMRNList } from "./RecentMRNList";
import {
  IPatientInformation,
  IRecentMRNLookup,
  IReferralInformation,
} from "@home-health-notify/domain/build/chat/emr/IEMRData";
import { GOOGLE_MAPS_API_KEY } from "../utils/GoogleMapsUtils";
import { FormHelperText } from "@mui/material";

type AddPatientInfoProps = {
  patientInformation?: IPatientInformation | undefined | null;
  fetchPatientDetails?: (mrn: string) => void;
  addPatientInfo: (patientInfo: IGroupInfo) => void;
  patientGroupInfo?: IGroupInfo;
  uploadFiles: any;
  setUploadFiles: (files: any[]) => void;
  fileCaptionMap: Map<string, string>;
  onSetCaption: (fileName: string, captionText: string) => void;
  onRemoveFile: (fileName: string) => void;
  isLoading: boolean;
  mode: "new" | "edit" | "episode";
  expandFilledAccordion?: boolean;
  isEmrSyncSupported?: boolean;
  recentMRNs?: IRecentMRNLookup[];
  recentMRNsLoading?: boolean;
};

export const AddPatientInfo = React.forwardRef<
  HTMLDivElement,
  AddPatientInfoProps
>(
  (
    {
      patientInformation,
      addPatientInfo,
      patientGroupInfo = {} as IGroupInfo,
      uploadFiles,
      setUploadFiles,
      fileCaptionMap,
      onSetCaption,
      onRemoveFile,
      isLoading,
      mode,
      fetchPatientDetails,
      expandFilledAccordion,
      isEmrSyncSupported = true,
      recentMRNs,
      recentMRNsLoading,
    },
    ref: React.Ref<HTMLDivElement>,
  ) => {
    const { t } = useTranslation();
    const tNs = (key: string) => t(key, { ns: "patientGroup" });

    // Use the shared form hook for all state, handlers, and validation
    const {
      updatedPatientGroupData,
      patientStatus,
      socStarter,
      lupaData,
      referralInfo,
      errors,
      onFormDataChange,
      onDobChange,
      onSetStatus,
      setSocStarter,
      onLupaDataChange,
      onReferralInfoChange,
      onAddInfoDataChange,
      onSetAbout,
      onSetInsurance,
      onSetMeta,
      onSetErrors,
      setUpdatedPatientGroupData,
      isFormInvalid,
      buildPayload,
    } = usePatientGroupForm({ mode, patientGroupInfo });

    // ─── Web-specific: EMR / MRN logic (not shared with mobile) ──────────

    const [mrn, setMRN] = useState<string>("");
    const [isSearchActive, setIsSearchActive] = useState(false);
    const [isPatientDetailsLoading, setIsPatientDetailsLoading] =
      useState<boolean>(false);
    const [emrReferralInformation, setEmrReferralInformation] =
      useState<IReferralInformation>();
    const [showEmrUnsupportedMessage, setShowEmrUnsupportedMessage] =
      useState(false);
    const [isRecentMRNsExpanded, setIsRecentMRNsExpanded] = useState(true);

    const currentPatientInformation = React.useRef<
      IPatientInformation | undefined
    >(patientInformation);

    useEffect(() => {
      (async () => {
        if (patientInformation !== currentPatientInformation.current) {
          currentPatientInformation.current = patientInformation;
          if (patientInformation) {
            setIsPatientDetailsLoading(true);
            const prepopulatedPatientData =
              await mapPatientInformationToPatientGroupData(
                patientInformation,
                updatedPatientGroupData,
                GOOGLE_MAPS_API_KEY,
                mrn,
              );
            setIsPatientDetailsLoading(false);
            setEmrReferralInformation(patientInformation.referral);
            setUpdatedPatientGroupData(prepopulatedPatientData);
          }
        }
      })();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patientInformation]);

    const runEmrSync = async (mrnValue: string) => {
      if (!mrnValue) return;
      if (!isEmrSyncSupported) {
        setShowEmrUnsupportedMessage(true);
        return;
      }
      setIsPatientDetailsLoading(true);
      setEmrReferralInformation(undefined);
      setUpdatedPatientGroupData(cloneDeep(defaultPatientInfoData));
      await fetchPatientDetails?.(mrnValue);
      setIsPatientDetailsLoading(false);
      setIsSearchActive(true);
    };

    const onSearchMRN = () => runEmrSync(mrn);

    const onMRNChange = (value: string) => {
      setIsSearchActive(false);
      setShowEmrUnsupportedMessage(false);
      setMRN(value);
    };

    const onSelectRecentMRN = (selectedMRN: string) => {
      setIsSearchActive(false);
      setShowEmrUnsupportedMessage(false);
      setMRN(selectedMRN);
      setIsRecentMRNsExpanded(false);
      runEmrSync(selectedMRN);
    };

    // ─── Web-specific: DOB handler wrapper (MUI DatePicker passes two args) ─

    const onDobChangeWeb = (newDate: any, _: any) => {
      onDobChange(newDate);
    };

    // Web-specific: checkDobValidation with side effects (sets errors)
    const checkDobValidation = (dateValue: string) => {
      const date_regex = /^\d{2}\/\d{2}\/\d{4}$/;
      const validDate: boolean = date_regex.test(dateValue);
      if (validDate) {
        onSetErrors("dob", false);
        const [month, day, year] = dateValue.split("/");
        if (+year < 1000) {
          onSetErrors("dob", true);
          return;
        }
        const dateObj = new Date(+year, +month - 1, +day);
        if (!isValidDateOfBirth(dateObj)) {
          onSetErrors("dob", true);
        }
      } else {
        onSetErrors("dob", true);
      }
    };

    // ─── Submit ──────────────────────────────────────────────────────────

    const onNextClick = () => {
      addPatientInfo(buildPayload());
    };

    const submitButtonLabel = isLoading
      ? t("buttonLabels.updating")
      : mode === "edit"
        ? t("buttonLabels.update")
        : t("buttonLabels.next");

    const isMRNNotFoundError = isSearchActive && !patientInformation;

    return (
      <AddPatientInfoLayout>
        <PatientGroupStatus
          onSetStatus={onSetStatus}
          status={String(patientStatus)}
        />
        <DrawerDivider />
        <FormControlLayout>
          {mode === "new" && (
            <SearchField>
              <VerticalLayout>
                <HorizontalLayout>
                  <InputMRN
                    mrn={mrn}
                    onChange={onMRNChange}
                    onEnterPress={onSearchMRN}
                  ></InputMRN>
                  <SyncButton
                    variant="contained"
                    loading={isPatientDetailsLoading}
                    onClick={onSearchMRN}
                    disabled={!mrn}
                  >
                    {tNs("syncFromEMR")}
                  </SyncButton>
                </HorizontalLayout>
                {isMRNNotFoundError && (
                  <FormHelperText error>{tNs("mrnNotFound")}</FormHelperText>
                )}
                {showEmrUnsupportedMessage && (
                  <FormHelperText error>
                    {tNs("emrSyncNotAvailable")}
                  </FormHelperText>
                )}
              </VerticalLayout>
            </SearchField>
          )}
          {recentMRNs && (
            <RecentMRNList
              recentMRNs={recentMRNs}
              loading={recentMRNsLoading}
              expanded={isRecentMRNsExpanded}
              onToggle={setIsRecentMRNsExpanded}
              onSelect={onSelectRecentMRN}
            />
          )}
          <InputMedicalRecordNumber
            patientInfoData={updatedPatientGroupData}
            onFormDataChange={onFormDataChange}
            onSetErrors={onSetErrors}
            errors={errors}
            required={false}
          />
          <InputFirstName
            patientInfoData={updatedPatientGroupData}
            onFormDataChange={onFormDataChange}
            onSetErrors={onSetErrors}
            errors={errors}
          />
          <InputLastName
            patientInfoData={updatedPatientGroupData}
            onFormDataChange={onFormDataChange}
            onSetErrors={onSetErrors}
            errors={errors}
          />
          <InputDOB
            date={updatedPatientGroupData.dob}
            onDobChange={onDobChangeWeb}
            checkDobValidation={checkDobValidation}
            errors={errors}
          />
          <InsuranceField
            insurance={updatedPatientGroupData.insurance}
            onSetInsurance={onSetInsurance}
          />
          <InputPatientTag
            meta={updatedPatientGroupData.meta}
            setMeta={onSetMeta}
          />
          <InputPhoneNumber
            patientInfoData={updatedPatientGroupData}
            onFormDataChange={onFormDataChange}
            onSetErrors={onSetErrors}
            errors={errors}
          />
          <InputApartmentNumber
            patientInfoData={updatedPatientGroupData}
            onFormDataChange={onFormDataChange}
          />
          <AddressField
            addressErrorMessage={errors["address"]}
            patientInfoData={updatedPatientGroupData}
            onFormDataChange={onFormDataChange}
            setAddressErrorMessage={(isError: boolean) =>
              onSetErrors("address", isError)
            }
          />
        </FormControlLayout>
        <DrawerDivider />
        <SocStarterField
          socStarter={socStarter}
          setRole={setSocStarter}
          groupInfo={updatedPatientGroupData}
          patientStatus={patientStatus}
        />
        <DrawerDivider />
        <AboutField
          about={updatedPatientGroupData.about}
          onSetAbout={onSetAbout}
        />
        <DrawerDivider />
        <LupaField lupaData={lupaData} onLupaDataChange={onLupaDataChange} />
        <DrawerDivider />
        <AddInfo
          onAddInfoDataChange={onAddInfoDataChange}
          addInfoData={updatedPatientGroupData.info}
          mode={mode}
          expandFilledAccordion={expandFilledAccordion}
        />
        <DrawerDivider />
        <ReferralInfoField
          onReferralInfoChange={onReferralInfoChange}
          referralInfo={referralInfo}
          uploadFiles={uploadFiles}
          setUploadFiles={setUploadFiles}
          fileCaptionMap={fileCaptionMap}
          onSetCaption={onSetCaption}
          onRemoveFile={onRemoveFile}
          ref={ref}
          referralSourceHintLabel={
            mode === "new" ? tNs("referralSourceHintLabel") : undefined
          }
          referralSourceHint={
            mode === "new"
              ? emrReferralInformation?.externalReferral
              : undefined
          }
          communityLiaisonHintLabel={
            mode === "new" ? tNs("communityLiaisonHintLabel") : undefined
          }
          communityLiaisonHint={
            mode === "new" ? emrReferralInformation?.referralSource : undefined
          }
        />
        <DrawerDivider />
        <SubmitButtonWrapper>
          <SetForthButton
            data-testid={"submit"}
            loading={isLoading}
            variant="contained"
            disabled={isFormInvalid}
            onClick={() => onNextClick()}
          >
            {submitButtonLabel}
          </SetForthButton>
        </SubmitButtonWrapper>
      </AddPatientInfoLayout>
    );
  },
);
